/**
 * generateTrack — the generation core's public entry point.
 *
 * Pure and deterministic: same (seed, catalog) → same Track, always.
 * Each subsystem draws only from its own labeled RNG family, so adding
 * systems later cannot perturb existing seeds' outputs.
 */
import type { Track, Vec3 } from "../shared/types.ts";
import { rngFamily } from "./rng.ts";
import { buildRegistry } from "./registry.ts";
import { generateSkeleton } from "./topology.ts";
import { planElevation } from "./elevation.ts";
import { pickStyle } from "./style.ts";
import { planFeatures } from "./features.ts";
import { realize } from "./wfc.ts";
import { assemble } from "./assemble.ts";
import { planStubs } from "./stubs.ts";
import { planTakeableSpurs } from "./takeable.ts";
import { planFans } from "./fans.ts";
import { planSceneryRing } from "./scenery.ts";
import {
  cellSetOracle,
  type DecorCatalog,
  pickSetting,
  placeDecor,
  regionAround,
} from "./decor.ts";
import { validateTrack } from "./validate.ts";

export interface GenerateOptions {
  /** Throw if validateTrack reports problems (on in tests/dev). */
  strict?: boolean;
  /** Skip decor (faster tests). */
  skipDecor?: boolean;
}

const registry = buildRegistry();

/** Single-track ambient margin — wide enough that the field fades into fog
 * instead of ending at a visible decor ring. */
const DECOR_MARGIN = 13;

export function generateTrack(
  seed: string,
  catalog: DecorCatalog = { packs: [] },
  opts: GenerateOptions = {},
): Track {
  const skeleton = generateSkeleton(rngFamily(seed, "topology"));
  const style = pickStyle(rngFamily(seed, "style"));
  // Features plan first (the ring is flat at plan time); elevation then
  // steers its hills around the feature-reserved cells. Helix-loop tracks
  // skip wide runs: the mandatory 2.0 climb must not lose its straight run
  // to the feature planner.
  const features = skeleton.helixLoops.length > 0
    ? []
    : planFeatures(rngFamily(seed, "features"), skeleton, style);
  const blocked = new Set<number>();
  for (const r of features) {
    for (let j = 0; j < r.len; j++) {
      blocked.add((r.start + j) % skeleton.cells.length);
    }
  }
  const elevation = planElevation(
    rngFamily(seed, "elevation"),
    skeleton,
    registry,
    blocked,
  );
  // Crossings are pre-reserved. Flat pierce: the 4-way cross mesh at the
  // first visit + the noMesh phantom at the second. Bridge pierce: the
  // upper visit (always the column, index-wise first) is the deck —
  // sometimes with a pierced floor, the marble's chance-fall onto the
  // channel below — and the lower visit is a plain straight.
  const bridge = skeleton.pierce?.bridge ?? false;
  const fallsRng = rngFamily(seed, "falls");
  const falls: { pos: Vec3; landPos: Vec3 }[] = [];
  const crossReservations = skeleton.crossings.flatMap(({ a, b }, i) => {
    if (!bridge) {
      return [
        { start: a, len: 1, pieceIds: ["cross"], reversed: false },
        { start: b, len: 1, pieceIds: ["cross-over"], reversed: false },
      ];
    }
    const hole = fallsRng.child(`hole/${i}`).chance(0.6);
    if (hole) {
      const c = skeleton.cells[a];
      falls.push({
        pos: [c.x, 1.25, c.z],
        landPos: [c.x, 0.25, c.z],
      });
    }
    return [
      {
        start: a,
        len: 1,
        pieceIds: [hole ? "straight-hole" : "straight"],
        reversed: false,
      },
      { start: b, len: 1, pieceIds: ["straight"], reversed: false },
    ];
  });
  const windows = realize(
    rngFamily(seed, "wfc"),
    skeleton,
    elevation,
    style,
    registry,
    [...features, ...crossReservations],
  );
  const assembled = assemble(skeleton, elevation, style, windows);

  const placements = [...assembled.placements];

  // Dead-end sidings: real split junctions in the ring with short branches.
  const stubs = planStubs(
    rngFamily(seed, "stubs"),
    placements,
    assembled.trackCells,
  );
  placements.push(...stubs.placements);
  for (const cell of stubs.cells) assembled.trackCells.add(cell);

  // Takeable spur branches (M13): an elevated split siding draining
  // through an end-hole onto the lower line — the marble takes it on
  // alternate laps (LoopSegment.alt) and the fall is guaranteed.
  const takeable = planTakeableSpurs(
    rngFamily(seed, "takeable"),
    skeleton,
    elevation,
    style,
    placements,
    assembled.loop.segments as import("../shared/types.ts").LoopSegment[],
    assembled.trackCells,
  );
  placements.push(...takeable.placements);
  falls.push(...takeable.falls);
  for (const cell of takeable.cells) assembled.trackCells.add(cell);

  // Trackside fans: deliberate machinery with a marble boost (M4).
  const fans = planFans(
    rngFamily(seed, "fans"),
    placements,
    assembled.trackCells,
  );
  placements.push(...fans.placements);
  for (const cell of fans.cells) assembled.trackCells.add(cell);
  // Curve-wide scenery ring (M13): the off-grid family as a freestanding
  // self-closed roundabout beside the line. Its cells join trackCells so
  // the decor field bands around it (a clearing with a landmark).
  const scenery = planSceneryRing(
    rngFamily(seed, "scenery"),
    assembled.trackCells,
  );
  placements.push(...scenery.placements);
  for (const cell of scenery.cells) assembled.trackCells.add(cell);

  const setting = pickSetting(rngFamily(seed, "setting"), catalog);
  if (!opts.skipDecor) {
    // Decor is a world field: single-track mode realizes it for the track's
    // bounds plus an ambient margin (the fog eats the region edge), with an
    // oracle over the one ring's cells. M5 swaps in chunk regions + a
    // chunk-backed oracle without touching placeDecor.
    placements.push(
      ...placeDecor(
        rngFamily(seed, "decor"),
        setting,
        style,
        regionAround(assembled.trackCells, DECOR_MARGIN),
        cellSetOracle(assembled.trackCells),
        catalog,
      ),
    );
  }

  const track: Track = {
    seed,
    placements,
    loop: assembled.loop,
    ...(falls.length > 0 ? { falls } : {}),
    stats: {
      theme: style.theme,
      paletteIndex: style.paletteIndex,
      setting: setting.name,
      pieceCount: placements.length,
      loopCells: skeleton.cells.length,
      maxDeckY: assembled.maxDeckY,
      families: [...new Set(placements.map((p) => p.pieceId))],
    },
  };

  if (opts.strict) {
    const errors = validateTrack(track);
    if (errors.length > 0) {
      throw new Error(
        `invalid track for seed "${seed}":\n${errors.join("\n")}`,
      );
    }
  }
  return track;
}

export { sampleLoop } from "./assemble.ts";
export { validateTrack } from "./validate.ts";
export type { DecorCatalog } from "./decor.ts";
